/**
 * Founder-only Coach Funnel dashboard (/admin/coach).
 *
 * Reads GET /api/admin/coach/metrics (internal-key gated, same convention as
 * AdminLogs) and renders the Reddit-ad → /coach → chat → email → subscription
 * funnel: stage counts (yesterday + rolling window), conversion rates, the tier
 * mix of coach leads, what visitors ask (keyword scan + recent questions), and
 * a per-day series. Mirrors the daily CEO digest's "Coach Funnel" section, live.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../common/Layout';
import { apiService, CoachMetrics } from '../../services/api';

const C = {
  bg: '#0e1116',
  card: '#161b22',
  sub: '#1c2230',
  border: '#2a3140',
  text: '#e6edf3',
  dim: '#9aa7b8',
  muted: '#6b7688',
  accent: '#5b8cff',
  green: '#3fb950',
  red: '#f85149',
  pill: '#222b3a',
};

const WINDOWS = [7, 14, 30];

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? '0' : n.toLocaleString();
const pct = (v: number | null): string => (v === null || v === undefined ? '—' : `${v.toFixed(0)}%`);

const CoachAnalytics: React.FC = () => {
  const [days, setDays] = useState<number>(7);
  const [data, setData] = useState<CoachMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await apiService.getCoachMetrics(days);
      setData(m);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('Access denied — REACT_APP_INTERNAL_KEY is missing or wrong for this build.');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load coach metrics.');
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  // The 5 canonical funnel stages, in order.
  const stages = useMemo(() => {
    if (!data) return [];
    const w = data.window;
    const d = data.primary;
    return [
      { label: 'Reached /coach', key: 'reached', yday: d.reached, win: w.reached },
      { label: '…from Reddit ad', key: 'reached_reddit', yday: d.reached_reddit, win: w.reached_reddit, sub: true },
      { label: 'Used the chat', key: 'chatted', yday: d.chatted, win: w.chatted },
      { label: 'Gave email', key: 'emailed', yday: d.emailed, win: w.emailed },
      { label: 'Subscribed (Pro/Premium)', key: 'subscribed', yday: d.subscribed, win: w.subscribed },
    ];
  }, [data]);

  const maxDay = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.by_day.map((r) => Math.max(r.reached, r.chatted, r.questions, r.emailed)));
  }, [data]);

  return (
    <Layout>
      <div style={{ background: C.bg, minHeight: '100vh', color: C.text, padding: '24px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Coach Funnel</h1>
              <p style={{ margin: '4px 0 0', color: C.dim, fontSize: 13 }}>
                Reddit ad → /coach → chat → email → subscription
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  onClick={() => setDays(w)}
                  style={{
                    background: days === w ? C.accent : C.sub,
                    color: days === w ? '#fff' : C.dim,
                    border: `1px solid ${days === w ? C.accent : C.border}`,
                    borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {w}d
                </button>
              ))}
              <button onClick={load} style={{ background: C.sub, color: C.dim, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
                ↻
              </button>
            </div>
          </div>

          {loading && <p style={{ color: C.dim }}>Loading…</p>}
          {error && (
            <div style={{ background: '#2a1416', border: `1px solid ${C.red}`, borderRadius: 8, padding: '12px 16px', color: '#ffb3ad', fontSize: 13, margin: '12px 0' }}>
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {/* Funnel */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px', marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 40, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, paddingRight: 4, marginBottom: 4 }}>
                  <span>Yesterday</span><span>{days}-day</span>
                </div>
                {stages.map((s) => (
                  <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderTop: `1px solid ${C.border}` }}>
                    <span style={{ color: s.sub ? C.muted : C.dim, fontSize: s.sub ? 12 : 14, paddingLeft: s.sub ? 14 : 0 }}>
                      {s.sub ? '↳ ' : ''}{s.label}
                    </span>
                    <span style={{ display: 'flex', gap: 40 }}>
                      <span style={{ width: 56, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(s.yday)}</span>
                      <span style={{ width: 56, textAlign: 'right', color: C.dim, fontVariantNumeric: 'tabular-nums' }}>{fmt(s.win)}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Rates + tiers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>Conversion ({days}d)</p>
                  <Row label="Reached → chatted" value={pct(data.rates.chat_rate)} />
                  <Row label="Chatted → emailed" value={pct(data.rates.email_rate)} />
                  <Row label="Reddit share of reach" value={pct(data.rates.reddit_share)} />
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>Coach leads by tier</p>
                  <Row label="Free" value={fmt(data.tiers.free)} />
                  <Row label="Pro" value={fmt(data.tiers.pro)} valueColor={C.green} />
                  <Row label="Premium" value={fmt(data.tiers.premium)} valueColor={C.green} />
                </div>
              </div>

              {/* By-day series */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginTop: 12 }}>
                <p style={{ margin: '0 0 12px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>
                  Daily (reached ▮ · chatted ▮ · emailed ▮)
                </p>
                {data.by_day.map((r) => (
                  <div key={r.day} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
                    <span style={{ width: 54, fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{r.day.slice(5)}</span>
                    <div style={{ flex: 1, display: 'flex', gap: 3, alignItems: 'center' }}>
                      <Bar n={r.reached} max={maxDay} color={C.accent} />
                      <Bar n={r.chatted} max={maxDay} color="#8957e5" />
                      <Bar n={r.emailed} max={maxDay} color={C.green} />
                    </div>
                    <span style={{ fontSize: 11, color: C.dim, fontVariantNumeric: 'tabular-nums', width: 90, textAlign: 'right' }}>
                      {r.reached}/{r.chatted}/{r.emailed}
                    </span>
                  </div>
                ))}
              </div>

              {/* What they ask */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginTop: 12 }}>
                <p style={{ margin: '0 0 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>What they ask about</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  {data.top_keywords.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>No questions logged yet.</span>}
                  {data.top_keywords.map((k) => (
                    <span key={k.term} style={{ background: C.pill, color: C.dim, borderRadius: 12, padding: '4px 11px', fontSize: 12 }}>
                      {k.term} <span style={{ color: C.text, fontWeight: 600 }}>{k.n}</span>
                    </span>
                  ))}
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted }}>Recent questions</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.recent_questions.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
                  {data.recent_questions.map((q, i) => (
                    <div key={i} style={{ background: C.sub, borderRadius: 8, padding: '9px 12px' }}>
                      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>“{q.text}”</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: C.muted }}>
                        Q#{q.question_number ?? '?'}
                        {q.utm_source ? ` · ${q.utm_source}` : ''}
                        {' · '}{new Date(q.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <p style={{ color: C.muted, fontSize: 11, marginTop: 14, lineHeight: 1.6 }}>
                Page-reach & chat tracking is server-side from{' '}
                {data.tracking_since ? new Date(data.tracking_since).toLocaleDateString() : 'first event'};
                email capture has full history. Raw ad clicks & spend live in Reddit Ads Manager + Plausible.
              </p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

const Row: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}>
    <span style={{ color: C.dim }}>{label}</span>
    <span style={{ color: valueColor || C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

const Bar: React.FC<{ n: number; max: number; color: string }> = ({ n, max, color }) => (
  <div
    title={`${n}`}
    style={{
      height: 12,
      width: `${Math.max(n > 0 ? 3 : 0, (n / max) * 100)}%`,
      background: color,
      borderRadius: 2,
      opacity: n > 0 ? 0.9 : 0,
      minWidth: n > 0 ? 3 : 0,
    }}
  />
);

export default CoachAnalytics;
